export const getFormData = (payload: any) => {
  const formData = new FormData();
  Object.keys(payload).forEach((key) => {
    formData.append(key, payload[key]);
  });
  return formData;
};

export const getExtension = (filename: string) => {
  return filename.split('.').pop();
};

export const getBlobImageSrc = async (url?: string): Promise<string | undefined> => {
  if (!url) return;

  const res = await fetch(url, { credentials: "omit" });
  const ct = res.headers.get("content-type")?.toLowerCase() ?? "";

  if (!res.ok) {
    const peek = await safePeekText(res);
    console.error(`[getBlobImageSrc] HTTP ${res.status} ${res.statusText}; ct=${ct}; peek=`, peek);
    return undefined;
  }

  // happy path: image content-type
  if (ct.startsWith("image/")) {
    const blob = await res.blob();
    const finalBlob = blob.type ? blob : new Blob([blob], { type: inferFromUrl(url) ?? "image/svg+xml" });
    return URL.createObjectURL(finalBlob);
  }

  // svg often comes as text/xml or text/plain — detect and coerce
  if (ct.includes("svg") || ct.includes("xml") || ct.includes("text/")) {
    const text = await res.text();
    if (/^\s*<svg[\s>]/i.test(text)) {
      return URL.createObjectURL(new Blob([text], { type: "image/svg+xml" }));
    }
    console.error("[getBlobImageSrc] Non-image textual response:", text.slice(0, 200));
    return undefined;
  }

  // unknown type: last ditch; if empty, bail
  const blob = await res.blob();
  if (!blob.size) {
    console.error("[getBlobImageSrc] Empty blob (size=0).");
    return undefined;
  }
  return URL.createObjectURL(new Blob([blob], { type: "image/svg+xml" }));
}

const inferFromUrl = (u: string): string | undefined => {
  const s = u.toLowerCase();
  if (s.includes(".svg")) return "image/svg+xml";
  if (s.includes(".png")) return "image/png";
  if (s.includes(".jpg") || s.includes(".jpeg")) return "image/jpeg";
  if (s.includes(".webp")) return "image/webp";
  if (s.includes(".gif")) return "image/gif";
  return undefined;
}

const safePeekText = async (res: Response): Promise<string> => {
  try { return (await res.clone().text()).slice(0, 200).replace(/\n/g, " "); }
  catch { return "(no preview)"; }
};

export const guessTypeFromUrl = (url: string): string | undefined => {
  const s = url.toLowerCase();
  if (s.includes(".svg")) return "image/svg+xml";
  if (s.includes(".png")) return "image/png";
  if (s.includes(".jpg") || s.includes(".jpeg")) return "image/jpeg";
  if (s.includes(".webp")) return "image/webp";
  if (s.includes(".gif")) return "image/gif";
  return undefined;
};

export type ApiError = {
  data?: { detail?: unknown };
  message?: string;
};

/**
 * Extracts a user-friendly message from common API error shapes.
 *
 * Args:
 *   error: Unknown error payload returned from API calls.
 *   defaultMessage: Optional fallback message when no detail is present.
 *
 * Returns:
 *   A string suitable for user-facing error presentation.
 */
export const getErrorMessage = (
  error: unknown,
  defaultMessage = "Please enter valid details"
): string => {
  if (typeof error === "string") {
    return error;
  }
  if (error && typeof error === "object") {
    const parsedError = error as ApiError;
    if (typeof parsedError.message === "string" && parsedError.message.trim()) {
      return parsedError.message;
    }
    const detail = parsedError.data?.detail;
    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }
    if (detail != null) {
      return String(detail);
    }
  }
  return defaultMessage;
};
